import AddressStep from 'features/contracts/add/steps/address/AddressStep.tsx'
import ProducerStep from 'features/contracts/add/steps/producer/ProducerStep.tsx'
import InstalmentStep from 'features/contracts/add/steps/instalment/InstalmentStep.tsx'
import MeterDetailsStep from 'features/contracts/add/steps/meter-details/MeterDetailsStep.tsx'
import CustomerDataStep from 'features/contracts/add/steps/customer-data/CustomerDataStep.tsx'
import OverviewStep from 'features/contracts/add/steps/overview/OverviewStep.tsx'
import Done from 'features/contracts/done/Done.tsx'
import ReferralStep from 'features/contracts/referral/ReferralStep.tsx'
import ProductStep from 'features/contracts/add/steps/product/ProductStep'
import { ReactElement, useEffect, useState } from 'react'
import { useBeforeunload } from 'react-beforeunload'
import { eanLookup } from 'lib/addContract'
import { ADD_CONTRACT_FLOW, GET_INITIAL_ADD_CONTRACT_DATA, MOVE_CONTRACT_FLOW } from './constants.ts'
import styles from '../Contracts.module.scss'
import { AddContractData, AddContractStepProps, AddContractSteps, CurrentStepData } from './types.ts'
import { EanLookupResponseBody } from 'lib/types'
import { routes } from 'types/routes'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { useGetLanguage } from 'hooks/use-get-language'
import InfoCard from 'features/contracts/add/components/info-card/InfoCard'
import { useLocation } from 'react-router'
import ContractsLayout from '../layout/ContractsLayout.tsx'
import { ContractFlows } from '../layout/types.ts'
import { useTranslation } from 'react-i18next'
import { producersBoltApi } from 'store/queries/bolt-api/producers'
import ConditionalCard from 'components/conditional-card/ConditionalCard.tsx'
import useWindowSize from 'hooks/useWindowSize.tsx'
import classnames from 'classnames'
import { AddressWithCountryAndTownCode } from 'types/general.ts'

const Add = () => {
  // REDUX STORE
  const { accounts, selected, contact } = useStoreSelector((store) => store.contact)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation('contracts')
  const language = useGetLanguage()
  const lowerCaseLanguage = useGetLanguage('lowercase')

  // Window size
  const { isTablet } = useWindowSize()

  // Local state
  const [currentStep, setCurrentStep] = useState<AddContractSteps>(AddContractSteps.ADDRESS)
  const [addContractData, setAddContractData] = useState<AddContractData>(
    GET_INITIAL_ADD_CONTRACT_DATA(language, accounts[selected.account], contact)
  )
  const [eanLookupData, setEanLookupData] = useState<EanLookupResponseBody>()

  // React Router
  const { pathname } = useLocation()

  // Constants
  const isMove = pathname === routes.CONTRACTS_MOVE
  const steps = isMove ? MOVE_CONTRACT_FLOW : ADD_CONTRACT_FLOW

  // Shows dialog when user tries to reload the page without confirming
  useBeforeunload((e: Event) => {
    if (currentStep !== AddContractSteps.DONE) e.preventDefault()
  })

  /**
   * Fetches producers on page load
   */
  useEffect(() => {
    // Initiate query
    const subscription = dispatch(producersBoltApi.endpoints.getProducers.initiate())

    // Clean up function
    return () => subscription.unsubscribe()
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  /**
   * Scroll to top of page every time currentStep changes
   */
  useEffect(() => {
    window.scrollTo({ top: 0 })

    return () => {
      window.scrollTo({ top: 0 })
    }
  }, [currentStep])

  /**
   * Handles closing of the add address flow
   */
  const handleExit = () => {
    setAddContractData(GET_INITIAL_ADD_CONTRACT_DATA(language, accounts[selected.account], contact))
    setCurrentStep(AddContractSteps.ADDRESS)
    setEanLookupData(undefined)
  }

  /**
   * Saves the current step data & goes to the next (given) step
   *
   * @param {CurrentStepData} currentStepData
   * @param {AddContractSteps} nextStep
   */
  const setNextStep = (currentStepData: CurrentStepData, nextStep: AddContractSteps) => {
    setAddContractData({
      ...addContractData,
      [currentStep]: currentStepData
    })
    setCurrentStep(nextStep)
  }

  /**
   * Handles the EAN lookup for the given address
   */
  const handleEanLookup = async (deliveryAddress: AddressWithCountryAndTownCode) => {
    const eanLookupData = await eanLookup(lowerCaseLanguage, deliveryAddress)

    setEanLookupData(eanLookupData)
  }

  /**
   * Returns the right component for the current step
   */
  const getCurrentStepComponent = (): ReactElement | null => {
    const stepProps = {
      isMove,
      addContractData,
      setAddContractData,
      setCurrentStep,
      setNextStep,
      handleEanLookup,
      eanLookupData
    } satisfies AddContractStepProps

    switch (currentStep) {
      //  STEP 1 - Address
      case AddContractSteps.ADDRESS:
        return <AddressStep {...stepProps} />

      // EXTRA MOVE STEP - Referral
      case AddContractSteps.REFERRAL:
        return (
          <ReferralStep
            defaultValues={addContractData[AddContractSteps.REFERRAL]}
            setCurrentStep={(step) => setCurrentStep((step as AddContractSteps) || AddContractSteps.PRODUCER)}
            setNextStep={(data) => setNextStep(data, AddContractSteps.PRODUCER)}
          />
        )

      // STEP 2 - Producer
      case AddContractSteps.PRODUCER:
        return <ProducerStep {...stepProps} />

      // STEP 3 - Product
      case AddContractSteps.PRODUCT:
        return <ProductStep {...stepProps} />

      // STEP 4 - Instalment
      case AddContractSteps.INSTALMENT_DATA:
        return <InstalmentStep {...stepProps} />

      // STEP 5 - Meter details
      case AddContractSteps.METER_DETAILS:
        return <MeterDetailsStep {...stepProps} />

      // STEP 6 - Customer data
      case AddContractSteps.CUSTOMER_DATA:
        return <CustomerDataStep {...stepProps} />

      // STEP 7 - Confirmation
      case AddContractSteps.CONFIRMATION:
        return <OverviewStep {...stepProps} />

      default:
        return null
    }
  }

  return (
    <ContractsLayout flow={ContractFlows.ADD} label={t(($) => $.header.labels.add)} {...{ steps, currentStep, setCurrentStep }}>
      {currentStep === AddContractSteps.DONE ? (
        <Done producer={addContractData.producer} onExit={handleExit} />
      ) : (
        <ConditionalCard
          asCard={!isTablet}
          className={classnames(styles['content-section'], {
            [styles.stretch]: currentStep === AddContractSteps.PRODUCER
          })}
        >
          {getCurrentStepComponent()}
        </ConditionalCard>
      )}

      {![AddContractSteps.ADDRESS, AddContractSteps.REFERRAL, AddContractSteps.PRODUCER, AddContractSteps.DONE].includes(currentStep) && (
        <InfoCard contractData={addContractData} {...{ currentStep }} />
      )}
    </ContractsLayout>
  )
}

export default Add
